import type { ServerSideGridArgs } from '@/components/CustomAgGrid/serverSide/gridRequest.ts';
import type { AnalyticsScope } from '@/features/analytics/analytics-scope.def.ts';
import type { BackendValuationBasisDto } from '@/features/analytics/valuation-basis.def.ts';
// Investor Profile Types

export const Geography = {
  NorthAmerica: 'North America',
  Europe: 'Europe',
  AsiaPacific: 'Asia-Pacific',
  MiddleEast: 'Middle East',
  LatinAmerica: 'Latin America',
  Africa: 'Africa',
} as const;

export type GeographyValue = (typeof Geography)[keyof typeof Geography];

/**
 * Every geography display string the API can return, including the legacy `EMEA` and `Americas (AMER)` values that
 * the edit forms no longer offer. Server-side grid Set Filters list these so stored rows stay selectable.
 */
export const GEOGRAPHY_FILTER_VALUES: readonly string[] = [
  ...Object.values(Geography),
  'EMEA',
  'Americas (AMER)',
];

export const InvestorType = {
  PensionPlan: { value: 'Pension Plan', shortTerm: null },
  FundOfFundsAndConsultants: {
    value: 'Fund of Funds and Consultants',
    shortTerm: 'FoFs and Consultants',
  },
  InternalCapital: {
    value: 'Internal Capital',
    shortTerm: null,
  },
  InsuranceAndBankAndOthers: {
    value: 'Insurance, Bank & Others',
    shortTerm: null,
  },
  SovereignWealthFund: {
    value: 'Sovereign Wealth Fund',
    shortTerm: 'SWF',
  },
  EndowmentsFoundations: {
    value: 'Endowments & Foundations',
    shortTerm: 'E&F',
  },
  FamilyOfficesAndHighIndividuals: {
    value: 'Family Offices & High-net-worth Individuals',
    shortTerm: 'FO & HNWI',
  },
} as const;

export type InvestorTypeValue = (typeof InvestorType)[keyof typeof InvestorType];

/** Extract just the value string from InvestorType (for use with backend data and filled forms) */
export type InvestorTypeStringValue = (typeof InvestorType)[keyof typeof InvestorType]['value'];

/** Get the short term display for an investor type, falling back to the full value if shortTerm is null */
export function getInvestorTypeShortTerm(value: InvestorTypeStringValue): string {
  const entry = Object.values(InvestorType).find((item) => item.value === value);
  return entry?.shortTerm ?? value;
}

export const InvestorStatus = {
  Active: 'Active',
  Prospect: 'Prospect',
  Inactive: 'Inactive',
} as const;

export type InvestorStatusValue = (typeof InvestorStatus)[keyof typeof InvestorStatus];

export const TransactionType = {
  Addition: 'Addition',
  TransferOut: 'Transfer Out',
  TransferIn: 'Transfer In',
  Redemption: 'Redemption',
  RedemptionPartial: 'Redemption - Partial',
  RedemptionFull: 'Redemption - Full',
  Withdrawal: 'Withdrawal',
  WithdrawalPartial: 'Withdrawal - Partial',
  WithdrawalFull: 'Withdrawal - Full',
} as const;

export type TransactionValue = (typeof TransactionType)[keyof typeof TransactionType];

export const isRedemptionTransactionType = (
  value: string | null | undefined,
): value is TransactionValue =>
  value === TransactionType.Redemption ||
  value === TransactionType.RedemptionPartial ||
  value === TransactionType.RedemptionFull ||
  value === TransactionType.Withdrawal ||
  value === TransactionType.WithdrawalPartial ||
  value === TransactionType.WithdrawalFull;

export interface InvestorProfile {
  /** Internal GUID primary key */
  id: string;
  /** Business identifier (e.g. "INV-00001") */
  displayId: string;
  name: string;
  city: string | null;
  country: string | null;
  geography: GeographyValue;
  investorType: InvestorTypeStringValue;
  status: string;
  legalNames: string[];
  accountCaptain: string;
  /** GUID of the account captain contact, null if none */
  accountCaptainId: string | null;
  netCommitment: number;
  latestNav: number | null;
}

export interface ContactPerson {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isActive: boolean;
}

export interface InvestmentRecord {
  id: string;
  transactionDate: string;
  type: TransactionValue;
  fund: string;
  commitment: number;
  inceptionPercent?: number | null;
  annualizedPercent?: number | null;
  ytdPercent?: number | null;
}

// Row type for the processed investment history table
export type InvestmentRowType = 'record' | 'subtotal' | 'reportingFundSubtotal' | 'grandTotal';

export interface InvestmentHistoryRow {
  id: string;
  rowType: InvestmentRowType;
  displayId?: string;
  investorName?: string;
  investorType?: InvestorTypeStringValue | null;
  status?: string;
  city?: string | null;
  country?: string | null;
  geography?: GeographyValue | null;
  transactionDate?: string;
  type?: TransactionValue;
  actionType?: string | null;
  fund: string;
  fundName?: string | null;
  shareClass: string;
  investorLegalName?: string;
  /** Legal-name-level location — distinct from the investor-level `city` / `country` above. */
  legalNameCity?: string | null;
  legalNameCountry?: string | null;
  commitment: number | null;
  reportingFundKey?: string | null;
  reportingFundId?: string | null;
  reportingFundOwnershipPercent?: number | null;
  reportingFundValuationDateFrom?: string | null;
  reportingFundValuationDateTo?: string | null;
  currentNav?: number | null;
  /** Subtotal rows: why `currentNav` is empty when the investor lags the fund's valuation date. */
  navHint?: string | null;
  /** Subtotal rows: links the fund name to Fund Detail. Null from an older backend. */
  fundId?: string | null;
  /** Subtotal rows: share of the fund's NAV on `valuationDate` (percent form). */
  ownershipPercent?: number | null;
  /** Subtotal rows: ISO `yyyy-MM-dd`, the fund-wide valuation date behind `ownershipPercent`. */
  valuationDate?: string | null;
  inceptionPercent?: number | null;
  annualizedPercent?: number | null;
  ytdPercent?: number | null;
  pnl?: number | null;
  irr?: number | null;
  cashFlowAmount?: number | null;
  transferDetails?: string | null;
}

// ── Backend DTO shapes (used in transformResponse) ──────────────────

/** Matches backend ContactPersonDto */
export interface BackendContactPersonDto {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isActive: boolean;
}

/** Matches backend InteractionSummaryDto */
export interface BackendInteractionSummaryDto {
  id: string;
  date: string;
  type: string;
  details: string;
  contactParticipantCount: number;
  irTeamParticipantCount: number;
}

/** Matches backend InvestmentSummaryMetricsDto */
export interface BackendInvestmentSummaryMetricsDto {
  netCommitment: number;
  fundCount: number;
  lastInvestmentDate: string | null;
}

/** Matches backend InvestorProfileDto */
export interface BackendInvestorProfileDto {
  id: string;
  displayId: string;
  name: string;
  city: string | null;
  country: string | null;
  geography: GeographyValue;
  type: string;
  status: string;
  legalNames: string[];
  accountCaptain: BackendContactPersonDto | null;
  netCommitment: number;
  /** The investor's NAV on each held fund's valuation date; equals investment-history `latestNav`. */
  latestNav: number | null;
  /**
   * Deprecated for display (B-02): the profile summary reads `fundCount` / `lastInvestmentDate` from the
   * investment history. Kept on the wire until it is removed server-side.
   */
  investmentSummary: BackendInvestmentSummaryMetricsDto | null;
}

export interface BackendLookupOptionDto {
  value: string;
  label: string;
}

export interface BackendInvestorLocationLookupDto {
  countries: BackendLookupOptionDto[];
  suggestedCities: string[];
  supportsCustomCityValue: boolean;
}

/** Matches backend ContactCommentDto */
export interface BackendContactCommentDto {
  id: string;
  text: string;
  createdBy: string;
  createdByDisplayName: string;
  createdAtUtc: string;
  updatedAt: string | null;
  isEdited: boolean;
  visibleToRoles: string[];
  visibleToUsernames: string[];
}

/** Matches backend InteractionContactParticipantDto */
export interface BackendInteractionContactParticipantDto {
  contactPersonId: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isActive: boolean;
}

/** Matches backend InteractionIrTeamParticipantDto */
export interface BackendInteractionIrTeamParticipantDto {
  username: string;
}

/** Matches backend InteractionDetailDto */
export interface BackendInteractionDetailDto {
  id: string;
  date: string;
  type: string;
  details: string;
  subject: string | null;
  emailSender: string | null;
  emailRecipient: string | null;
  emailCc: string | null;
  plainTextContent: string | null;
  combinedText: string | null;
  sourceInteractionId: string | null;
  createdBy: string;
  createdByDisplayName: string;
  createdAtUtc: string;
  updatedBy: string | null;
  updatedByDisplayName: string | null;
  updatedAt: string | null;
  isEdited: boolean;
  contactParticipants: BackendInteractionContactParticipantDto[];
  irTeamParticipants: BackendInteractionIrTeamParticipantDto[];
}

/** Matches backend IrTeamMemberDto */
export interface BackendIrTeamMemberDto {
  username: string;
  email: string;
  nickname: string;
  department: string;
  region: string;
  roles: string[];
}

// ── Backend Investment History DTOs (hierarchical) ──────────────────

/** Matches backend InvestmentTransactionRowDto */
export interface BackendInvestmentTransactionRowDto {
  id: string;
  transactionDate: string;
  commitment: number;
  type: string;
  fund: string;
  shareClass: string;
  investorLegalName: string;
  /**
   * The legal entity's own city / country, resolved by the backend from the investor's legal-name
   * records. An investor may hold through several legal names in different places, so these are
   * distinct from the investor-level `city` / `country`. Null when the legal name on the
   * transaction matches no stored legal name.
   */
  investorLegalNameCity: string | null;
  investorLegalNameCountry: string | null;
  /** Share class's current NAV; only set on the first-series row of each share class. */
  currentNav: number | null;
  pnl: number;
  irr: number | null;
  cashFlowAmount: number;
  transferDetails?: string | null;
}

/** Matches backend FundInvestmentGroupDto */
export interface BackendFundInvestmentGroupDto {
  /** The fund's id, for the Fund Detail link on subtotal rows. Optional: older payloads omit it. */
  fundId?: string | null;
  fund: string;
  subtotalCommitment: number;
  /** The investor's NAV on the fund's valuation date; null when they have no row on that date. */
  currentNav: number | null;
  inceptionPercent: number | null;
  annualizedPercent: number | null;
  ytdPercent: number | null;
  pnl: number | null;
  irr: number | null;
  cashFlowAmount: number;
  transactions: BackendInvestmentTransactionRowDto[];
  /** Percent form (25 = 25%) of the fund's NAV on its valuation date. Shown on fund subtotal rows. */
  ownershipPercent?: number | null;
  /** ISO `yyyy-MM-dd`; the fund's valuation date across every investor. */
  valuationDate?: string | null;
  /** ISO `yyyy-MM-dd`; this investor's latest NAV date for the fund on or before the cut-off. */
  lastNavDate?: string | null;
}

/** Matches backend ReportingFundOwnershipDto. */
export interface BackendReportingFundOwnershipDto {
  groupKey: string;
  reportingFundId: string | null;
  reportingFund: string;
  fundIds: string[];
  currentNav: number | null;
  ownershipPercent: number | null;
  valuationDateFrom: string | null;
  valuationDateTo: string | null;
}

/** Matches backend InvestorInvestmentHistoryDto. */
export interface BackendInvestorInvestmentHistoryDto {
  id: string;
  displayId: string;
  name: string;
  city: string | null;
  country: string | null;
  geography: GeographyValue;
  status: string;
  type: InvestorTypeStringValue;
  netCommitment: number;
  latestNav: number | null;
  inceptionPercent: number | null;
  annualizedPercent: number | null;
  pnl?: number | null;
  irr?: number | null;
  cashFlowAmount: number;
  fundGroups: BackendFundInvestmentGroupDto[];
  /**
   * The investor's legal-name mapping with each legal name's own city / country. Independent of the
   * transactions, so an investor with no commitments still reports its legal names.
   */
  legalNames?: BackendInvestorLegalNameSummaryDto[] | null;
  /**
   * The investor's active consultants, already ordered by the API. Null when the endpoint does not
   * supply them — the profile investment-history view does not, only the dashboard does.
   */
  consultants?: BackendInvestorConsultantSummaryDto[] | null;
  /** Distinct funds with a transaction on or before the cut-off. */
  fundCount?: number | null;
  /** ISO `yyyy-MM-dd`; latest transaction on or before the cut-off. */
  lastInvestmentDate?: string | null;
  /** Valuation dates of the funds this investor holds. */
  valuation?: BackendValuationBasisDto | null;
  /** Optional server-derived reporting-fund ownership summaries; null/omitted keeps legacy rows. */
  reportingFundOwnership?: BackendReportingFundOwnershipDto[] | null;
  /** Investor NAV as a percentage of scoped Total AUM; populated by dashboard records only. */
  inScopeAumOwnershipPercent?: number | null;
}

/** Matches backend InvestorLegalNameSummaryDto */
export interface BackendInvestorLegalNameSummaryDto {
  name: string;
  city: string | null;
  country: string | null;
}

/** Matches backend InvestorConsultantSummaryDto */
export interface BackendInvestorConsultantSummaryDto {
  name: string;
}

export interface LookupOption {
  value: string;
  label: string;
}

// ── Backend Dashboard DTOs ──────────────────────────────────────────

/** Matches backend InvestorsDashboardSummaryDto */
export interface BackendDashboardSummaryDto {
  /** Investors with a NAV row on a scoped fund's valuation date (holders). */
  totalInvestors: number;
  /** Sum of every scoped fund's NAV on its valuation date; equals NAV Breakdown `totalNav`. */
  totalAum: number;
  activeCount: number;
  prospectCount: number;
  inactiveCount: number;
  /** "No NAV yet": investors with no NAV row on or before the cut-off in any fund. Fund-independent. */
  noNavCount?: number;
  valuation?: BackendValuationBasisDto | null;
}

/** Page scope plus the server-side grid page, sort and filter (`sort`/`filter` follow the backend grid contract). */
export interface GetInvestorsDashboardRecordsRequest extends AnalyticsScope, ServerSideGridArgs {
  /**
   * Also return `totals`. Sent only when true: the backend sums encrypted amounts in memory, so
   * export chunks leave it off and keep the SQL path.
   */
  includeTotals?: boolean;
}

/**
 * Dashboard grid columns whose Set Filter values come from the API
 * (backend `GetInvestorsDashboardFilterValuesQuery.Columns`). `geographyGroup` is not a grid column:
 * it serves the scope bar and returns the reporting-group keys present (`Americas`, `Emea`, `AsiaPacific`).
 */
export type InvestorDashboardFilterValuesColumn =
  | 'investorSummary'
  | 'investorName'
  | 'displayId'
  | 'consultant'
  | 'fundName'
  | 'shareClass'
  | 'city'
  | 'country'
  | 'investorLegalName'
  | 'legalNameCity'
  | 'legalNameCountry'
  | 'transferDetails'
  | 'geographyGroup';

export interface GetInvestorsDashboardFilterValuesRequest extends AnalyticsScope {
  column: InvestorDashboardFilterValuesColumn;
}

/** Matches backend InvestorsDashboardNavMonthsDto — ISO `yyyy-MM` values, newest first. */
export interface BackendDashboardNavMonthsDto {
  months: string[];
}

/**
 * Matches backend InvestorDashboardTotalsDto: the five additive columns summed over every investor
 * matching the scope and grid filters, as the grid shows them. Member names are the grid column ids.
 * The nullable sums are absent when no matching investor has a value (the API omits nulls).
 */
export interface BackendInvestorDashboardTotalsDto {
  commitment: number;
  currentNav?: number | null;
  pnl?: number | null;
  cashFlowAmount: number;
  inScopeAumOwnershipPercent?: number | null;
}

/** Matches backend InvestorDashboardRecordsPage (paginated) */
export interface BackendDashboardRecordsDto {
  items: BackendInvestorInvestmentHistoryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** Present only when the request set `includeTotals` (absent from an older API and the dry run). */
  totals?: BackendInvestorDashboardTotalsDto | null;
}

// API Response types (transformed from backend DTOs)
// The summary now comes from the investment history (L1-07).
export interface InvestorProfileResponse {
  profile: InvestorProfile;
}

// Interaction Types

export const InteractionType = {
  Meeting: 'Meeting',
  Call: 'Call',
  Email: 'Email',
  Meal: 'Meal',
  Coffee: 'Coffee',
  Drinks: 'Drinks',
  Conference: 'Conference',
  Webinar: 'Webinar',
  SiteVisit: 'Site Visit',
  Other: 'Other',
  // Imported FolioMetrics call notes; matches the backend InteractionType.Note display name.
  Note: 'Note',
} as const;

export type InteractionTypeValue = (typeof InteractionType)[keyof typeof InteractionType];

export interface InteractionContactParticipant {
  contactPersonId: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isActive: boolean;
}

export interface InteractionIrTeamParticipant {
  username: string;
}

export interface InteractionParticipantDisplayItem {
  key: string;
  label: string;
  subLabel?: string;
  kind: 'contact' | 'ir-team';
  isInactive?: boolean;
}

export interface Interaction {
  id: string;
  date: string;
  interactionType: InteractionTypeValue;
  details: string;
  subject: string | null;
  emailSender: string | null;
  emailRecipient: string | null;
  emailCc: string | null;
  plainTextContent: string | null;
  combinedText: string | null;
  sourceInteractionId: string | null;
  createdBy: string;
  createdByDisplayName: string;
  createdAt: string;
  updatedBy: string | null;
  updatedByDisplayName: string | null;
  updatedAt: string | null;
  isEdited: boolean;
  contactParticipants: InteractionContactParticipant[];
  irTeamParticipants: InteractionIrTeamParticipant[];
  participants: InteractionParticipantDisplayItem[];
}

export type InteractionsResponse = Interaction[];

export interface IrTeamMember {
  username: string;
  email: string;
  nickname: string;
  department: string;
  region: string;
  roles: string[];
}

export type IrTeamMembersResponse = IrTeamMember[];

export interface CreateInteractionRequest {
  investorId: string;
  date: string;
  interactionType: InteractionTypeValue;
  contactPersonIds: string[];
  irTeamUsernames: string[];
  details: string;
  subject?: string | null;
  emailSender?: string | null;
  emailRecipient?: string | null;
  emailCc?: string | null;
  plainTextContent?: string | null;
  combinedText?: string | null;
}

export type CreateInteractionResponse = Interaction;

export interface UpdateInteractionRequest {
  investorId: string;
  interactionId: string;
  date: string;
  interactionType: InteractionTypeValue;
  contactPersonIds: string[];
  irTeamUsernames: string[];
  details: string;
  subject?: string | null;
  emailSender?: string | null;
  emailRecipient?: string | null;
  emailCc?: string | null;
  plainTextContent?: string | null;
  combinedText?: string | null;
}

export type UpdateInteractionResponse = void;

export interface DeleteInteractionRequest {
  investorId: string;
  interactionId: string;
}

export type DeleteInteractionResponse = void;

// Contact Person CRUD Types

export interface GetContactPersonsRequest {
  investorId: string;
  includeInactive: boolean;
}

export interface CreateContactPersonRequest {
  investorId: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

export interface CreateContactPersonResponse {
  data: ContactPerson;
}

export interface UpdateContactPersonRequest {
  investorId: string;
  contactId: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

export type UpdateContactPersonResponse = void;

export interface DeleteContactPersonRequest {
  investorId: string;
  contactId: string;
}

export type DeleteContactPersonResponse = void;

// Contact Comment Types

export interface ContactComment {
  id: string;
  text: string;
  createdBy: string;
  createdByDisplayName: string;
  createdAt: string;
  updatedAt: string | null;
  isEdited: boolean;
  /** Roles that can view this comment. Empty array = visible to all IR team. */
  visibleToRoles: string[];
  /** Usernames that can view this comment. Empty array = no username-specific restriction. */
  visibleToUsernames: string[];
}

export type ContactCommentsResponse = ContactComment[];

export interface GetContactCommentsRequest {
  investorId: string;
  contactId: string;
}

export interface CreateContactCommentRequest {
  investorId: string;
  contactId: string;
  text: string;
  visibleToRoles: string[];
  visibleToUsernames: string[];
}

export interface CreateContactCommentResponse {
  data: ContactComment;
}

export interface UpdateContactCommentRequest {
  investorId: string;
  contactId: string;
  commentId: string;
  text: string;
  visibleToRoles: string[];
  visibleToUsernames: string[];
}

export type UpdateContactCommentResponse = void;

export interface DeleteContactCommentRequest {
  investorId: string;
  contactId: string;
  commentId: string;
}

export type DeleteContactCommentResponse = void;

// ── Investor Activity History ────────────────────────────────────────

export type ActivityFieldDecryptionStatus = 'NotRequested' | 'Decrypted' | 'Unavailable';

export interface ActivityFieldChange {
  fieldLabel: string;
  oldValue?: string | null;
  newValue?: string | null;
  isEncrypted?: boolean;
  decryptionStatus?: ActivityFieldDecryptionStatus;
  decryptedOldValue?: string | null;
  decryptedNewValue?: string | null;
}

export interface InvestorActivityEvent {
  auditId: number;
  changedAtUtc: string;
  operation: 'Created' | 'Updated' | 'Deleted';
  entityType: string;
  entityLabel?: string | null;
  actorUsername?: string | null;
  fieldChanges: ActivityFieldChange[];
}

export interface InvestorActivityHistoryResponse {
  items: InvestorActivityEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export type ActivityEntityType =
  | 'investor'
  | 'contact_person'
  | 'commitment'
  | 'redemption'
  | 'investor_nav'
  | 'interaction';

export interface GetInvestorActivityHistoryRequest {
  investorId: string;
  page?: number;
  pageSize?: number;
  fromUtc?: string;
  toUtc?: string;
  entityType?: ActivityEntityType;
  operation?: 'Created' | 'Updated' | 'Deleted';
}

// Investor Profile Update Types

export interface UpdateInvestorProfileRequest {
  /** Internal GUID primary key — used in the URL path */
  investorId: string;
  name: string;
  geography: GeographyValue;
  type: string;
  status: string;
  legalNames: string[];
  accountCaptainId: string | null;
  city?: string | null;
  country?: string | null;
}

export type UpdateInvestorProfileResponse = void;

// ── Investor Dashboard / List Types ─────────────────────────────────

/** Lightweight row for the investor list / dashboard grid */
export interface InvestorListItem {
  /** Internal GUID primary key */
  id: string;
  /** Business identifier (e.g. "INV-00001") */
  displayId: string;
  name: string;
  city: string | null;
  country: string | null;
  geography: GeographyValue;
  investorType: InvestorTypeStringValue;
  status: InvestorStatusValue;
  netCommitment: number;
  latestNav: number | null;
  contactCount: number;
}

export interface BackendInvestorSummaryDto {
  id: string;
  displayId: string;
  name: string;
  city: string | null;
  country: string | null;
  geography: GeographyValue;
  type: string;
  status: string;
  netCommitment: number;
  latestNav: number | null;
  contactCount: number;
}

export interface BackendInvestorsListDto {
  items: BackendInvestorSummaryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** Response shape for GET /investors */
export type InvestorListResponse = InvestorListItem[];

/** Row type discriminator for the dashboard AG Grid tree data */
export type DashboardGridRowType =
  | 'investor'
  | 'record'
  | 'fundSubtotal'
  | 'reportingFundSubtotal'
  | 'grandTotal';

/**
 * One dashboard tree row, for every hierarchy level. Client mode (the dry-run preview) passes flat
 * rows and reads the hierarchy from `path`; server-side mode nests rows through `children`.
 */
export interface DashboardGridRow {
  /** AG Grid tree data hierarchy path (client-side tree). */
  path: string[];
  /** Unique across the whole tree; the server-side row id and group key. */
  rowId: string;
  /** Inline child rows (server-side tree): reporting parents/fund subtotals under investors and descendants below. */
  children?: DashboardGridRow[];
  rowType: DashboardGridRowType;
  /** Investor-level fields (null for non-investor rows) */
  displayId: string | null;
  investorName?: string | null;
  investorGuid: string | null;
  name: string | null;
  investorType: InvestorTypeStringValue | null;
  status: string | null;
  city: string | null;
  country: string | null;
  geography: GeographyValue | null;
  investorLegalName: string | null;
  /** Legal-name-level location — distinct from the investor-level `city` / `country` above. */
  legalNameCity: string | null;
  legalNameCountry: string | null;
  /** Consultant firms advising the investor, joined for display. Dashboard only. */
  consultant: string | null;
  /** Record/subtotal-level fields */
  actionType: string | null;
  fundName: string | null;
  shareClass: string | null;
  /** Shared numeric fields */
  commitment: number | null;
  pnl: number | null;
  irr: number | null;
  cashFlowAmount: number | null;
  currentNav: number | null;
  /** Fund subtotal rows: why `currentNav` is empty when the investor lags the fund's valuation date. */
  navHint?: string | null;
  /** Fund subtotal rows: links the fund name to Fund Detail. Null from an older backend. */
  fundId?: string | null;
  /** Fund subtotal rows: share of the fund's NAV on `valuationDate` (percent form). */
  ownershipPercent?: number | null;
  /** Investor-parent rows: share of the active analytics scope's total AUM (percent form). */
  inScopeAumOwnershipPercent?: number | null;
  /** Fund subtotal rows: ISO `yyyy-MM-dd`, the fund-wide valuation date behind `ownershipPercent`. */
  valuationDate?: string | null;
  /** Reporting-fund subtotal rows: stable server-provided ownership grouping key. */
  reportingFundKey?: string | null;
  reportingFundId?: string | null;
  reportingFundOwnershipPercent?: number | null;
  reportingFundValuationDateFrom?: string | null;
  reportingFundValuationDateTo?: string | null;
  inceptionPercent: number | null;
  annualizedPercent: number | null;
  ytdPercent: number | null;
  /** Transactions-level fields **/
  transactionDate?: string;
  transferDetails?: string | null;
}

/** Request for creating a new investor */
export interface CreateInvestorRequest {
  name: string;
  geography: GeographyValue;
  type: InvestorTypeStringValue;
  status?: InvestorStatusValue;
  legalNames?: string[];
  city?: string | null;
  country?: string | null;
}

export type CreateInvestorResponse = InvestorProfileResponse;

// ── Investor Interaction Dashboard Types (US-005) ────────────────────

// Backend DTO shape (snake_case from API)
export interface BackendInvestorInteractionRowDto {
  investorId: string;
  displayId: string;
  name: string;
  investorType: string;
  status: string;
  geography: string;
  commitmentValue: number | null;
  latestInteractionDate: string | null; // ISO instant string
  latestInteractionType: string | null;
  interactionCount: number;
  funds: string[];
}

export interface BackendInvestorInteractionsListDto {
  items: BackendInvestorInteractionRowDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Frontend mapped type
export interface InvestorInteractionRow {
  investorId: string;
  displayId: string;
  name: string;
  investorType: string;
  status: string;
  geography: string;
  commitmentValue: number | null;
  latestInteractionDate: Date | null;
  latestInteractionType: string | null;
  interactionCount: number;
  funds: string[];
}

export interface InvestorInteractionsResponse {
  items: InvestorInteractionRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** Page scope of the investor interactions list (the filter panel); shared by the list and filter-values routes. */
export interface InvestorInteractionsScope {
  types?: string[]; // InvestorType enum values
  funds?: string[]; // Fund reference-data GUIDs
  minCommitment?: number;
  interactionTypes?: string[]; // InteractionType enum values
  interactionFrom?: string;
  interactionTo?: string;
}

/** Page scope plus the server-side grid page, sort and filter (`sort`/`filter` follow the backend grid contract). */
export interface GetInvestorInteractionsRequest
  extends InvestorInteractionsScope, ServerSideGridArgs {}

/** Investor interactions grid columns whose Set Filter values come from the API. */
export type InvestorInteractionFilterValuesColumn = 'name' | 'funds';

export interface GetInvestorInteractionFilterValuesRequest extends InvestorInteractionsScope {
  column: InvestorInteractionFilterValuesColumn;
}

/**
 * A pending or applied city/country change for a single legal name.
 *
 * Shared by both upload flows because the shape and the meaning are identical: the location belongs
 * to the legal entity (custodian / nominee), not to the investor. The investor workbook sources it
 * from the per-row "Legal Name City" / "Legal Name Country" columns; the investment workbook resolves
 * it from the NAV sheet's monthly snapshots down to a single latest value.
 *
 * A null on either side means "no value", not "unchanged" — an entry only exists when something
 * actually changed.
 */
export interface LegalNameLocationChangeDto {
  legalName: string;
  oldCity: string | null;
  newCity: string | null;
  oldCountry: string | null;
  newCountry: string | null;
}
